import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import './Users.css';
import config from "../config";
import { useBpFlags } from "../Responsive";

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);
function Users() {

  const { isTablet } = useBpFlags();
  const viewportRef = useRef(null);
  const contentRef  = useRef(null);
  const [scale, setScale] = useState(1);
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendInfo, setSendInfo] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [planPerms, setPlanPerms] = useState(null);
  const [newUser, setNewUser] = useState({
    id: null,
    username: "",
    password: "",
    name: "",
    phone: "",
    email: "",
    comment: "",
    view_admin: false,
    view_reservations_list: false,
    view_reservation_form: false,
    view_bbq: false,
    view_kitchen: false,
    view_service: false,
    view_users: false,
    view_settings: false,
    view_tables: false,
    view_reports: false,
    view_scheduler: false,
    view_schedule: false,
    view_billing: false,
    view_clients: false,
    at_management: false,
    at_bbq: false,
    at_kitchen: false,
    at_cleaning: false,
    at_service: false,
    push_notifications: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    axios
      .get(tr('{{API_BASE_URL}}/users/plan-permissions', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((res) => {
        const { active, permissions } = res.data || {};
  
        if (!active || !permissions) {
          // No valid subscription -> plan gives no view_* permissions
          setPlanPerms({});
          return;
        }
  
        const normalize = (v) => {
          if (v === null || v === undefined) return false;
          if (v && v.data) return !!v.data[0]; // BIT(1) from MySQL
          return !!v;
        };
  
        setPlanPerms({
          // only view_* fields (admin column stays special)
          view_reservations_list: normalize(permissions.view_reservations_list),
          view_reservation_form:  normalize(permissions.view_reservation_form),
          view_bbq:               normalize(permissions.view_bbq),
          view_kitchen:           normalize(permissions.view_kitchen),
          view_service:           normalize(permissions.view_service),
          view_schedule:          normalize(permissions.view_schedule),
          view_scheduler:         normalize(permissions.view_scheduler),
          view_reports:           normalize(permissions.view_reports),
          view_clients:           normalize(permissions.view_clients),
          view_settings:          normalize(permissions.view_settings),
          view_tables:            normalize(permissions.view_tables),
          view_users:             normalize(permissions.view_users),
          view_billing:           normalize(permissions.view_billing),
        });
      })
      .catch((err) => {
        console.error("Error fetching plan-permissions:", err);
        // safest fallback: hide everything except Admin
        setPlanPerms({});
      });
  }, []);

  // Tablet: auto-fit the Users grid so it doesn't push the bottom navbar.
  useEffect(() => {
    if (!isTablet) { setScale(1); return; }
    const compute = () => {
      const vp = viewportRef.current;
      const el = contentRef.current;
      if (!vp || !el) return;
      const vw = vp.clientWidth - 16;             // a little breathing room
      const cw = el.scrollWidth;                   // natural content width (min 1000px from CSS)
      const s = Math.max(0.75, Math.min(1, vw / Math.max(1, cw))); // clamp to 0.75..1
      setScale(s);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [isTablet, users, sendInfo, sending]);

  const fetchUsers = () => {
    axios.get(tr('{{API_BASE_URL}}/users', {
      API_BASE_URL: config.API_BASE_URL
    }))
      .then((response) => {
        const cleanedUsers = response.data.map(user => ({
          id: user.id,
          username: user.username || "",
          password: "",
          name: user.name || "",
          phone: user.phone || "",
          email: user.email || "",
          comment: user.comment || "",
          view_admin: !!user.view_admin?.data?.[0],
          view_reservations_list: !!user.view_reservations_list?.data?.[0],
          view_reservation_form: !!user.view_reservation_form?.data?.[0],
          view_bbq: !!user.view_bbq?.data?.[0],
          view_kitchen: !!user.view_kitchen?.data?.[0],
          view_service: !!user.view_service?.data?.[0],
          view_users: !!user.view_users?.data?.[0],
          view_settings: !!user.view_settings?.data?.[0],
          view_tables: !!user.view_tables?.data?.[0],
          view_reports: !!user.view_reports?.data?.[0],
          view_scheduler: !!user.view_scheduler?.data?.[0],
          view_schedule: !!user.view_schedule?.data?.[0],
          view_billing:  !!user.view_billing?.data?.[0],
          view_clients:  !!user.view_clients?.data?.[0],
          at_management: !!user.at_management?.data?.[0],
          at_bbq: !!user.at_bbq?.data?.[0],
          at_kitchen: !!user.at_kitchen?.data?.[0],
          at_cleaning: !!user.at_cleaning?.data?.[0],
          at_service: !!user.at_service?.data?.[0],
          push_notifications: !!user.push_notifications?.data?.[0]
        }));
        setUsers(cleanedUsers);
      })
      .catch((error) => console.error("Error fetching users:", error));
  }

  const handleEditUser = (user) => {
    if (editingUser && editingUser.id !== user.id) {
      const confirmChange = window.confirm(
        "You have unsaved changes. Do you want to save them before switching users?"
      );
  
      if (confirmChange) {
        handleSaveUser(editingUser.id);
      } else {
        fetchUsers(); // Reload users to discard unsaved changes
      }
      setEditingUser(null);
      return;
    }
    setEditingUser(user);
  };

  const handleSaveUser = async (id) => {
    try {
      const response = await axios.put(tr('{{API_BASE_URL}}/users/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id
      }), editingUser);
      if (response.data.error)
        alert(response.data.error);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const response = await axios.delete(tr('{{API_BASE_URL}}/users/{{id}}', {
        API_BASE_URL: config.API_BASE_URL,
        id
      }));
      if (response.data.error) 
        alert(response.data.error);
      fetchUsers();
    } catch (error) {
      alert(error.response.data.error);
    }
  };

  const handleAddUser = async () => {
    try {
      await axios.post(tr('{{API_BASE_URL}}/users', {
        API_BASE_URL: config.API_BASE_URL
      }), newUser);
      setNewUser({
        username: "",
        password: "",
        name: "",
        phone: "",
        email: "",
        comment: "",
        view_admin: 0,
        view_reservations_list: 0,
        view_reservation_form: 0,
        view_bbq: 0,
        view_kitchen: 0,
        view_service: 0,
        view_users: 0,
        view_settings: 0,
        view_tables: 0,
        view_reports: 0,
        view_scheduler: 0,
        view_schedule: 0,
        view_billing: 0,
        view_clients: 0,
        at_management: 0,
        at_bbq: 0,
        at_kitchen: 0,
        at_cleaning: 0,
        at_service: 0,
        push_notifications: 0
      });
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };

  async function handleNotifyAll() {
    if (!window.confirm("Send a test notification to all subscribers in this company?")) return;
    try {
      setSending(true);
      setSendInfo(null);
      const resp = await axios.post(
        tr('{{API_BASE_URL}}/notifications/test-all', {
          API_BASE_URL: config.API_BASE_URL
        }),
        {},
        { withCredentials: true }
      );
      setSendInfo(resp.data);
    } catch (e) {
      console.error("Notify error:", e);
      setSendInfo({ error: e.response?.data?.error || "Failed to send" });
    } finally {
      setSending(false);
    }
  }

  // All view_* in the order your table expects:
  const ALL_VIEW_PERMS = [
    "view_admin",
    "view_reservations_list",
    "view_reservation_form",
    "view_bbq",
    "view_kitchen",
    "view_service",
    "view_schedule",
    "view_scheduler",
    "view_reports",
    "view_clients",
    "view_settings",
    "view_tables",
    "view_users",
    "view_billing",
  ];

  // Which view_* should we actually show?
  const isViewPermVisible = (perm) => {
    // Admin column is always visible
    if (perm === "view_admin") return true;
  
    // Before the plan is loaded, show everything (optional: change to false if you
    // prefer to hide until it's loaded).
    if (!planPerms) return true;
  
    return !!planPerms[perm];
  };

  const visibleViewPerms = ALL_VIEW_PERMS.filter(isViewPermVisible);

  const ROLE_PERMS = [
    "at_management",
    "at_bbq",
    "at_kitchen",
    "at_cleaning",
    "at_service",
    "push_notifications",
  ];

  // Show scheduler-related stuff (roles + test notifications)
  // If planPerms not loaded yet, keep them visible; hide only if the plan explicitly says "no"
  const showSchedulerFeatures = !planPerms || !!planPerms.view_scheduler;

  return (
    // Outer viewport: reserves space for the bottom navbar and scrolls within
    <div className={isTablet ? 'tablet-viewport' : ''} ref={viewportRef}>
      {/* Scale wrapper: we inject --scale from state */}
      <div className={isTablet ? 'fit-scale-wrap' : ''} style={isTablet ? { '--scale': scale } : undefined}>
        {/* This inner div keeps your existing min-width and all styles, but gets scaled */}
        <div className={isTablet ? 'users-container fit-scale' : 'users-container'} ref={contentRef}>
          <div className="users-header">
            <h2>{tr('Users Management')}{showSchedulerFeatures && (
                <button
                  className="notify-btn"
                  onClick={handleNotifyAll}
                  disabled={sending}
                >
                  {sending ? "Notifying..." : "Send Test Notifications"}
                </button>
              )}
            </h2>
          </div>
          {sendInfo && (
            <div className="notify-result">
              {sendInfo.ok
                ? <>{tr('Sent:')}<b>{sendInfo.success}</b>{tr('• Failed:')}<b>{sendInfo.failure}</b>{tr('• Total:')}<b>{sendInfo.total}</b></>
                : <>{tr('Error:')}{sendInfo.error}</>
              }
            </div>
          )}
          <i style={{color: 'blue', fontSize: '11px', top: '60px', right: '5px', position: 'absolute'}}>{tr(
            '* For a NEW user, Username & Password are required. For updates, password is optional.'
          )}</i>
          <i style={{color: 'green', fontSize: '11px', top: '75px', right: '95px', position: 'absolute'}}>{tr(
            '* At least one \'Administartor\' needed! Administrators have full access.'
          )}</i>
          <i style={{color: 'red', fontSize: '11px', top: '90px', right: '90px', position: 'absolute'}}>{tr(
            '* Access to \'Reservation Form\' requires access to \'Reservations List\' too.'
          )}</i>
          {showSchedulerFeatures && (
            <i
              style={{
                color: 'darkOrange',
                fontSize: '11px',
                top: '105px',
                right: '190px',
                position: 'absolute'
              }}
            >{tr('* Push notifications requires a role @ Service too.')}</i>
          )}

          <table className="users-table">
            <thead>
              <tr>
                <th className="horizontal-header">{tr('Username')}<i style={{color: 'blue', fontSize: '12px'}}>*</i>
                </th>
                <th className="horizontal-header">{tr('Password')}<i style={{color: 'blue', fontSize: '12px'}}>*</i>
                </th>
                <th className="horizontal-header">{tr('Name')}</th>
                <th className="horizontal-header">{tr('Phone')}</th>
                <th className="horizontal-header">{tr('Email')}</th>
                <th className="horizontal-header">{tr('Comment')}</th>

                {/* View permissions (only those allowed by plan) */}
                {visibleViewPerms.includes("view_admin") && (
                  <th className="vertical-header">{tr('Administrator')}<i style={{color: 'green', fontSize: '12px'}}>*</i>
                  </th>
                )}
                {visibleViewPerms.includes("view_reservations_list") && (
                  <th className="vertical-header">{tr('Reservations List')}<i style={{color: 'red', fontSize: '12px'}}>*</i>
                  </th>
                )}
                {visibleViewPerms.includes("view_reservation_form") && (
                  <th className="vertical-header">{tr('Reservation Form')}<i style={{color: 'red', fontSize: '12px'}}>*</i>
                  </th>
                )}
                {visibleViewPerms.includes("view_bbq") && (
                  <th className="vertical-header">{tr('BBQ')}</th>
                )}
                {visibleViewPerms.includes("view_kitchen") && (
                  <th className="vertical-header">{tr('Kitchen')}</th>
                )}
                {visibleViewPerms.includes("view_service") && (
                  <th className="vertical-header">{tr('Service')}</th>
                )}
                {visibleViewPerms.includes("view_schedule") && (
                  <th className="vertical-header">{tr('Schedule')}</th>
                )}
                {visibleViewPerms.includes("view_scheduler") && (
                  <th className="vertical-header">{tr('Scheduler')}</th>
                )}
                {visibleViewPerms.includes("view_reports") && (
                  <th className="vertical-header">{tr('Reports')}</th>
                )}
                {visibleViewPerms.includes("view_clients") && (
                  <th className="vertical-header">{tr('Clients')}</th>
                )}
                {visibleViewPerms.includes("view_settings") && (
                  <th className="vertical-header">{tr('Settings')}</th>
                )}
                {visibleViewPerms.includes("view_tables") && (
                  <th className="vertical-header">{tr('Tables')}</th>
                )}
                {visibleViewPerms.includes("view_users") && (
                  <th className="vertical-header">{tr('Users')}</th>
                )}
                {visibleViewPerms.includes("view_billing") && (
                  <th className="vertical-header">{tr('Billing')}</th>
                )}

                {/* Employee roles — only if scheduler is in plan */}
                {showSchedulerFeatures && (
                  <>
                    <th className="vertical-header role">{tr('@ Management')}</th>
                    <th className="vertical-header role">{tr('@ BBQ')}</th>
                    <th className="vertical-header role">{tr('@ Kitchen')}</th>
                    <th className="vertical-header role">{tr('@ Cleaning')}</th>
                    <th className="vertical-header role">{tr('@ Service')}<i style={{color: 'darkOrange', fontSize: '12px'}}>*</i>
                    </th>
                    <th className="vertical-header role">{tr('Service Notifications')}<i style={{color: 'darkOrange', fontSize: '12px'}}>*</i>
                    </th>
                  </>
                )}

                <th className="horizontal-header"></th>
              </tr>

              <tr>
                <th colSpan="2" style={{background: '#eef'}}>{tr('Account Credentials')}</th>
                <th colSpan="4" style={{background: '#fee'}}>{tr('User Details')}</th>

                {/* Number of view-permission columns actually visible */}
                <th colSpan={visibleViewPerms.length} style={{background: '#efe'}}>{tr('View Permissions')}</th>

                {/* Roles group only if scheduler features are enabled */}
                {showSchedulerFeatures && (
                  <th colSpan={ROLE_PERMS.length} style={{background: '#fed'}}>{tr('Employee Roles')}</th>
                )}

                <th style={{background: '#eff'}}>{tr('Actions')}</th>
              </tr>

            </thead>
            <tbody>
              {users.map(user => {
                return (
                  <tr key={user.id} style={{background: '#efd'}}>
                    <>
                          <td>
                            <input
                              type="text"
                              placeholder={tr('Set Username')}
                              value={(editingUser?.id === user.id ? editingUser : user).username}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), username: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="password"
                              placeholder={tr('Set new password')}
                              autoComplete="new-password"
                              value={(editingUser?.id === user.id ? editingUser : user).password}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), password: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder={tr('Set Name')}
                              value={(editingUser?.id === user.id ? editingUser : user).name}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), name: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={(editingUser?.id === user.id ? editingUser : user).phone}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), phone: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={(editingUser?.id === user.id ? editingUser : user).email}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), email: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={(editingUser?.id === user.id ? editingUser : user).comment}
                              onChange={(e) =>
                                handleEditUser({ ...(editingUser?.id === user.id ? editingUser : user), comment: e.target.value })
                              }
                            />
                          </td>
                          {[...visibleViewPerms, ...(showSchedulerFeatures ? ROLE_PERMS : [])].map(
                            (perm, i) => {
                              const current = editingUser?.id === user.id ? editingUser : user;
                              return (
                                <td key={perm}>
                                  <input
                                    className="checkbox-column"
                                    type="checkbox"
                                    checked={!!current[perm]}
                                    disabled={i && user.view_admin} // keep your admin rule
                                    title={perm.replaceAll('_', ' ')}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      const updated = { ...current, [perm]: checked };

                                      if (perm === "view_reservation_form" && updated.view_reservation_form)
                                        updated.view_reservations_list = true;
                                      else if (perm === "view_reservations_list" && !updated.view_reservations_list)
                                        updated.view_reservation_form = false;
                                      else if (perm === "push_notifications" && updated.push_notifications)
                                        updated.at_service = true;
                                      else if (perm === "at_service" && !updated.at_service)
                                        updated.push_notifications = false;

                                      handleEditUser(updated);
                                    }}
                                  />
                                </td>
                              );
                            }
                          )}

                          <td>
                            <button
                              // username required; password optional for updates
                              disabled={!editingUser || user.id !== editingUser.id || !editingUser.username}
                              onClick={() => handleSaveUser(user.id)}
                            >{tr('Update')}</button>
                            {' '}<button onClick={() => handleDeleteUser(user.id)}>{tr('Delete')}</button>
                          </td>
                      </>
                  </tr>
                );
              })}
              <tr style={{background: '#def'}}>
                <td>
                  <input
                    type="text"
                    placeholder={tr('Username')}
                    value={newUser.username}
                    onChange={(e) => 
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                  />
                </td>
                <td>
                  <input
                    type="password"
                    placeholder={tr('Password')}
                    autoComplete="new-password"
                    value={newUser.password}
                    onChange={(e) => 
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder={tr('Name')}
                    value={newUser.name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, name: e.target.value })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder={tr('Phone')}
                    value={newUser.phone}
                    onChange={(e) =>
                      setNewUser({ ...newUser, phone: e.target.value })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder={tr('Email')}
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    placeholder={tr('Comment')}
                    value={newUser.comment}
                    onChange={(e) =>
                      setNewUser({ ...newUser, comment: e.target.value })
                    }
                  />
                </td>
                {[...visibleViewPerms, ...(showSchedulerFeatures ? ROLE_PERMS : [])].map(
                  (perm, i) => (
                    <td key={perm}>
                      <input
                        type="checkbox"
                        checked={!!newUser[perm]}
                        title={perm.replaceAll('_', ' ')}
                        disabled={i && newUser.view_admin}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const updated = { ...newUser, [perm]: checked };

                          if (perm === "view_reservation_form" && updated.view_reservation_form)
                            updated.view_reservations_list = true;
                          else if (perm === "view_reservations_list" && !updated.view_reservations_list)
                            updated.view_reservation_form = false;
                          else if (perm === "push_notifications" && updated.push_notifications)
                            updated.at_service = true;
                          else if (perm === "at_service" && !updated.at_service)
                            updated.push_notifications = false;

                          setNewUser(updated);
                        }}
                      />
                    </td>
                  )
                )}
                
                <td>
                  <button disabled={!newUser.username || !newUser.password} onClick={handleAddUser} style={{width: '110px'}}>{tr('Add New User')}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Users;
